import CryptoJS from "crypto-js";
import CryptoWorker from "@/shared/crypto.worker.js";
import getEnv from "@/utilities/env.js";
import appsignal from "@/plugins/appsignal";
import { Tables, CRYPTO_KEY } from "@/shared/consts.js";

const DB_NAME = getEnv("VUE_APP_LOCAL_DATABASE");
const DB_VERSION = getEnv("VUE_APP_LOCAL_DATABASE_VERSION");
let DB;

// Bulk AES work (the ~3,000-row employee list) runs in a worker so it cannot
// block the UI; see SUPP-9155. Single-entity calls stay synchronous: one row
// is cheap and the user record is needed before anything else renders.
let cryptoWorker = null;
let nextCryptoJobId = 0;
const pendingCryptoJobs = new Map();

function failPendingCryptoJobs(error) {
  pendingCryptoJobs.forEach(job => job.reject(error));
  pendingCryptoJobs.clear();
}

function getCryptoWorker() {
  if (cryptoWorker) return cryptoWorker;
  if (typeof Worker === "undefined") return null;

  try {
    cryptoWorker = new CryptoWorker();
  } catch (e) {
    return null;
  }

  cryptoWorker.onmessage = e => {
    const { id, result, error } = e.data || {};
    const job = pendingCryptoJobs.get(id);
    if (!job) return;
    pendingCryptoJobs.delete(id);
    if (error) job.reject(new Error(error));
    else job.resolve(result);
  };

  cryptoWorker.onerror = e => {
    // Drop the worker; callers fall back to the main thread for this call and
    // a fresh worker is created on the next one.
    const error = new Error((e && e.message) || "crypto worker failed");
    appsignal.sendError(error);
    cryptoWorker.terminate();
    cryptoWorker = null;
    failPendingCryptoJobs(error);
  };

  return cryptoWorker;
}

function runCryptoInWorker(action, items) {
  return new Promise((resolve, reject) => {
    const id = ++nextCryptoJobId;
    pendingCryptoJobs.set(id, { resolve, reject });
    getCryptoWorker().postMessage({ id, action, items });
  });
}

async function runCrypto(action, items, syncFn) {
  if (items.length === 0) return [];

  if (getCryptoWorker()) {
    try {
      return await runCryptoInWorker(action, items);
    } catch (e) {
      // fall through to the synchronous path below
    }
  }

  return items.map(syncFn);
}

export default {
  isEncrypted(entityName) {
    return ["user", "employees", "users"].includes(entityName);
  },
  encryptEntity(entityData) {
    return CryptoJS.AES.encrypt(
      JSON.stringify(entityData),
      CRYPTO_KEY
    ).toString();
  },

  decryptEnitity(entityData) {
    var bytesData = CryptoJS.AES.decrypt(entityData, CRYPTO_KEY);
    return JSON.parse(bytesData.toString(CryptoJS.enc.Utf8));
  },

  encryptEntitiesAsync(entities) {
    return runCrypto("encrypt", entities, this.encryptEntity);
  },

  decryptEntitiesAsync(values) {
    return runCrypto("decrypt", values, this.decryptEnitity);
  },
  async openDataBase() {
    return new Promise((resolve, reject) => {
      if (DB) {
        return resolve(DB);
      }
      let request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = e => {
        appsignal.sendError(e);
        reject("Error");
      };

      request.onsuccess = e => {
        DB = e.target.result;
        return resolve(DB);
      };

      request.onupgradeneeded = e => {
        let db = e.target.result;
        db.createObjectStore(Tables.TAILBOARD_FORMS);
        db.createObjectStore(Tables.TAILBOARD_FORM_SUBMISSIONS);
        db.createObjectStore(Tables.ARCHIVED_TAILBOARD_FORM_SUBMISSIONS);
        let users = db.createObjectStore(Tables.USERS);
        db.createObjectStore(Tables.USER);
        users.createIndex("employeeId", "employee.id", { unique: true });
        db.createObjectStore(Tables.HAZARDS);
        db.createObjectStore(Tables.HAZARD_CATEGORIES);
        db.createObjectStore(Tables.BARRIERS);
        db.createObjectStore(Tables.WORK_TYPES);
        db.createObjectStore(Tables.EMPLOYEES);
        db.createObjectStore(Tables.CRITICAL_TASKS);
        db.createObjectStore(Tables.VEHICLES);
        db.createObjectStore(Tables.UPDATED_ENTITIES);
        db.createObjectStore(Tables.DELETED_CHILDS_QUEUE);
        db.createObjectStore(Tables.TAILBOARDS_QUEUE);
        let assets = db.createObjectStore(Tables.TRAFFIC_ASSETS);
        assets.createIndex("type", "type", { unique: false });
        db.createObjectStore(Tables.FEEDERS);
        db.createObjectStore(Tables.STRUCTURES);
      };
    });
  },

  // Entity methods

  async deleteAllAsync(entityName) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readwrite");
      trans.oncomplete = () => {
        return resolve(true);
      };

      let store = trans.objectStore(entityName);
      store.clear();
    });
  },

  async deleteEntityById(id, entityName) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readwrite");
      trans.oncomplete = () => {
        return resolve(true);
      };

      let store = trans.objectStore(entityName);
      store.delete(id);
    });
  },

  async saveEntitiesAsync(entities, entityName) {
    let db = await this.openDataBase();
    const values = this.isEncrypted(entityName)
      ? await this.encryptEntitiesAsync(entities)
      : entities;

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readwrite");
      trans.oncomplete = () => {
        return resolve(true);
      };

      let store = trans.objectStore(entityName);
      entities.forEach((entity, i) => {
        store.put(values[i], entity.id);
      });
    });
  },

  async saveEntityByIdAsync(entity, entityName) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readwrite");
      trans.oncomplete = () => {
        return resolve(true);
      };

      let store = trans.objectStore(entityName);
      store.put(
        this.isEncrypted(entityName) ? this.encryptEntity(entity) : entity,
        entity.id
      );
    });
  },

  async saveEntityAsync(key, value, entityName) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readwrite");
      trans.oncomplete = () => {
        return resolve(true);
      };

      let store = trans.objectStore(entityName);
      store.put(value, key);
    });
  },

  async getEntitiesAsync(entity) {
    const rawValues = await this.getRawValuesAsync(entity);
    return this.isEncrypted(entity)
      ? this.decryptEntitiesAsync(rawValues)
      : rawValues;
  },

  async getRawValuesAsync(entity) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entity], "readonly");
      trans.oncomplete = () => {
        return resolve(rawValues);
      };

      let store = trans.objectStore(entity);
      let rawValues = [];
      const request = store.openCursor();
      request.onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          rawValues.push(cursor.value);
          cursor.continue();
        }
      };
    });
  },

  async getEntityAsync(entityName) {
    let db = await this.openDataBase();

    return new Promise(resolve => {
      let trans = db.transaction([entityName], "readonly");
      trans.oncomplete = () => {
        return resolve(entityObject);
      };

      let store = trans.objectStore(entityName);
      let entityObject = {};

      store.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          entityObject = this.isEncrypted(entityName)
            ? this.decryptEnitity(cursor.value)
            : cursor.value;
          cursor.continue();
        }
      };
    });
  },

  async getEntityByIdAsync(entityName, id) {
    let db = await this.openDataBase();
    return new Promise(resolve => {
      let transaction = db.transaction([entityName], "readonly");
      transaction.oncomplete = () => {
        return resolve(entityObject);
      };
      let objectStore = transaction.objectStore(entityName);
      let entityObject = objectStore.get(id);
    });
  },

  async getEntityByIndexAsync(entityName, indexName, id) {
    let db = await this.openDataBase();
    return new Promise(resolve => {
      let transaction = db.transaction([entityName], "readonly");

      transaction.oncomplete = () => {
        return resolve(entityObject);
      };
      let objectStore = transaction.objectStore(entityName);
      let objectIndex = objectStore.index(indexName);
      let entityObject = objectIndex.get(id);
    });
  },

  async getEntityIndexAsync(entityName, indexName, expression) {
    let db = await this.openDataBase();
    return new Promise(resolve => {
      let transaction = db.transaction([entityName], "readonly");

      transaction.oncomplete = () => {
        return resolve(entityObject);
      };
      let objectStore = transaction.objectStore(entityName);
      let objectIndex = objectStore.index(indexName);
      let entityObject = [];
      objectIndex.openCursor().onsuccess = e => {
        let cursor = e.target.result;
        if (cursor) {
          if (cursor.value[indexName] === expression)
            entityObject.push(cursor.value);
          cursor.continue();
        }
      };
    });
  },

  async getEncryptedEntityByIndexAsync(entityName, indexName, id) {
    try {
      const entities = await this.getEntitiesAsync(entityName);
      const match = entities.find(
        entityObject =>
          entityObject &&
          entityObject[indexName] &&
          entityObject[indexName].id === id
      );
      return { result: match || null };
    } catch (e) {
      console.error("Error searching in cursor");
      return { result: null };
    }
  },

  async isExistAsync(storeName) {
    let db = await this.openDataBase();
    return db.objectStoreNames.contains(storeName);
  }
};
